import { useContext, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, AuthContext } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { useHeartbeat } from './hooks/useHeartbeat';
import "./styles/_tokens.scss";
import "./styles/theme.scss";

// ── Pages ─────────────────────────────────────────────────────────────────────
import Login             from "./pages/login/Login";
import SelecionarCliente from "./pages/login/SelecionarCliente";
import Usuarios          from "./pages/usuarios/Usuarios";
import Produtos          from "./pages/produtos/Produtos";
import Dashboard         from "./pages/dashboard/Dashboard";
import Venda             from "./pages/venda/Venda";
import Estoque           from "./pages/estoque/Estoque";
import Entrada           from "./pages/entrada/Entrada";
import Cliente           from "./pages/cliente/ClientePage";
import OrcamentoPage     from "./pages/orcamento/OrcamentoPage";
import DocumentacaoPage  from "./pages/documetacao/DocumentacaoPage";
import SobrePage         from "./pages/sobre/SobrePage";
import Bloqueado         from "./pages/bloqueado/Bloqueado";
import Calendario        from "./pages/calendario/Calendario";
import Aluguel           from "./pages/aluguel/Aluguel";
import Precificacao      from "./pages/precificacao/Precificacao";
import SubClientePage    from "./pages/sub-cliente/SubClientepage";
import HistoricoVendas   from "./pages/historicoVenda/HistoricoVenda";
import Etiqueta          from "./pages/etiqueta/Etiqueta"

// ── Components ────────────────────────────────────────────────────────────────
import Header from "./components/header/Header";
import Footer from "./components/footer/Footer";
import { useWakeServer } from "./hooks/useWakeServer";
import FaturamentoCompleto from "./pages/faturamento/FaturamentoCompleto";

// ─── Roles ────────────────────────────────────────────────────────────────────

// Perfis que existem no sistema
const ROLES = {
  SUPERADMIN: "superadmin",
  ADMIN:      "admin",
  VENDEDOR:   "vendedor",
  ESTOQUE:    "estoque",
};

// Mapa de quais perfis podem acessar cada rota protegida.
// Ausência da rota = somente admin/superadmin.
const ROUTE_ROLES = {
  "/produtos":         [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.ESTOQUE],
  "/entrada":          [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.ESTOQUE],
  "/estoque":          [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.ESTOQUE],
  "/Aluguel":          [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.VENDEDOR],
  "/venda":            [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.VENDEDOR],
  "/historico-vendas": [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.VENDEDOR],
  // Apenas admin / superadmin:
  "/inicial":          [ROLES.ADMIN, ROLES.SUPERADMIN],
  "/faturamento":      [ROLES.ADMIN, ROLES.SUPERADMIN],
  "/cliente":          [ROLES.ADMIN, ROLES.SUPERADMIN],
  "/orcamento":        [ROLES.ADMIN, ROLES.SUPERADMIN],
  "/calendario":       [ROLES.ADMIN, ROLES.SUPERADMIN],
  "/meus-clientes":    [ROLES.ADMIN, ROLES.SUPERADMIN],
  "/usuarios":         [ROLES.ADMIN, ROLES.SUPERADMIN],
  "/precificacao":     [ROLES.ADMIN, ROLES.SUPERADMIN],
  "/etiqueta":         [ROLES.ADMIN, ROLES.SUPERADMIN],
  "/selecionar-cliente": [ROLES.SUPERADMIN],
};

// Rota padrão por perfil (para onde redirecionar após login ou acesso negado)
const HOME_BY_ROLE = {
  [ROLES.SUPERADMIN]: "/selecionar-cliente",
  [ROLES.ADMIN]:      "/venda",
  [ROLES.VENDEDOR]:   "/venda",
  [ROLES.ESTOQUE]:    "/estoque",
};

const homeForUser = (user) =>
  HOME_BY_ROLE[user?.nivel_acesso] ?? "/inicial";

// ─── Loader ───────────────────────────────────────────────────────────────────

const loaderStyles = {
  container: {
    display: "flex", alignItems: "center", justifyContent: "center",
    height: "100vh", width: "100vw", backgroundColor: "#f0f2f5",
  },
  card: {
    backgroundColor: "#ffffff", borderRadius: "12px",
    padding: "40px 60px", boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    textAlign: "center", fontSize: "1.1rem", color: "#444",
  },
};

// ─── App ──────────────────────────────────────────────────────────────────────

function App() {
  const { ready, warming } = useWakeServer();

  if (!ready) {
    return (
      <div style={loaderStyles.container}>
        <div style={loaderStyles.card}>
          {warming ? <p>☕ Servidor iniciando, aguarde...</p> : <p>Conectando...</p>}
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Layout />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

// ─── Layout ───────────────────────────────────────────────────────────────────

const AUTH_PATHS = new Set(["/", "/acesso-bloqueado"]);

const Layout = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  useHeartbeat();

  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/service-worker.js");
    });
  }

  const isAuthPage = AUTH_PATHS.has(location.pathname);
  const showChrome = !isAuthPage && Boolean(user);

  return (
    <div className="app-shell">
      {showChrome && <Header />}

      <main className={isAuthPage ? "app-main app-main--auth" : "app-main"}>
        <Routes>
          {/* ── Públicas ──────────────────────────────────────────────────── */}
          <Route path="/"                  element={<Login />} />
          <Route path="/acesso-bloqueado"  element={<Bloqueado />} />
          <Route path="/documentacao"      element={<DocumentacaoPage />} />
          <Route path="/sobre"             element={<SobrePage />} />

          {/* ── Privadas com controle de perfil ───────────────────────────── */}
          <Route path="/selecionar-cliente" element={
            <Guard path="/selecionar-cliente"><SelecionarCliente /></Guard>
          } />
          <Route path="/inicial"            element={
            <Guard path="/inicial"><Dashboard /></Guard>
          } />
          <Route path="/usuarios"           element={
            <Guard path="/usuarios"><Usuarios /></Guard>
          } />
          <Route path="/precificacao"       element={
            <Guard path="/precificacao"><Precificacao /></Guard>
          } />
          <Route path="/entrada"            element={
            <Guard path="/entrada"><Entrada /></Guard>
          } />
          <Route path="/produtos"           element={
            <Guard path="/produtos"><Produtos /></Guard>
          } />
          <Route path="/venda"              element={
            <Guard path="/venda"><Venda /></Guard>
          } />
          <Route path="/historico-vendas"   element={
            <Guard path="/historico-vendas"><HistoricoVendas /></Guard>
          } />
          <Route path="/estoque"            element={
            <Guard path="/estoque"><Estoque /></Guard>
          } />
          <Route path="/cliente"            element={
            <Guard path="/cliente"><Cliente /></Guard>
          } />
          <Route path="/orcamento"          element={
            <Guard path="/orcamento"><OrcamentoPage /></Guard>
          } />
          <Route path="/calendario"         element={
            <Guard path="/calendario"><Calendario /></Guard>
          } />
          <Route path="/Aluguel"            element={
            <Guard path="/Aluguel"><Aluguel /></Guard>
          } />
          <Route path="/meus-clientes"      element={
            <Guard path="/meus-clientes"><SubClientePage /></Guard>
          } />
          <Route path="/etiqueta"      element={
            <Guard path="/etiqueta"><Etiqueta /></Guard>
          } />
          <Route path="/faturamento"      element={
            <Guard path="/faturamento"><FaturamentoCompleto /></Guard>
          } />

          {/* ── Fallback ──────────────────────────────────────────────────── */}
          <Route path="*" element={<FallbackRedirect />} />
        </Routes>
      </main>

      {!isAuthPage && <Footer />}
    </div>
  );
};

// ─── Guard ────────────────────────────────────────────────────────────────────

/**
 * Guard: verifica autenticação + permissão de perfil.
 *
 * - Não autenticado          → /
 * - Autenticado sem permissão → home do perfil (ex: vendedor → /venda)
 * - OK                       → renderiza children
 */
const Guard = ({ path, children }) => {
  const { user, loading } = useContext(AuthContext);

  if (loading) return null;

  // Não autenticado
  if (!user) return <Navigate to="/" replace />;

  const allowed = ROUTE_ROLES[path];

  // Rota não tem restrição definida → só admin/superadmin
  const permittedRoles = allowed ?? [ROLES.ADMIN, ROLES.SUPERADMIN];

  if (!permittedRoles.includes(user.nivel_acesso)) {
    // Redireciona para a home correta do perfil sem mostrar erro
    return <Navigate to={homeForUser(user)} replace />;
  }

  return children;
};

// ─── FallbackRedirect ─────────────────────────────────────────────────────────

/** Rota desconhecida: se logado vai para a home do perfil, senão para o login */
const FallbackRedirect = () => {
  const { user } = useContext(AuthContext);
  return <Navigate to={user ? homeForUser(user) : "/"} replace />;
};

export default App;